/**
 * Browser-side image processing for profile photos.
 * - Center-crops to a square
 * - Resizes to 400x400
 * - Encodes as JPEG at quality 0.85
 *
 * This produces consistent, ~50–100KB photos no matter what the user uploads,
 * with no server-side image library needed.
 */

const TARGET_SIZE = 400;
const JPEG_QUALITY = 0.85;
const MAX_INPUT_SIZE_MB = 15;

export async function processProfilePhoto(file) {
  if (!file) throw new Error('No file provided');
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image (JPEG, PNG, HEIC, etc.)');
  }
  if (file.size > MAX_INPUT_SIZE_MB * 1024 * 1024) {
    throw new Error(`Image too large. Max ${MAX_INPUT_SIZE_MB} MB.`);
  }

  // Load file into an Image element so we can draw it on canvas
  const img = await loadImage(file);

  // Center-crop to a square based on the smaller dimension
  const minDim = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - minDim) / 2;
  const sy = (img.naturalHeight - minDim) / 2;

  // Draw to a 400x400 canvas
  const canvas = document.createElement('canvas');
  canvas.width = TARGET_SIZE;
  canvas.height = TARGET_SIZE;
  const ctx = canvas.getContext('2d');

  // Use higher-quality smoothing for downscaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Background fill (in case image has transparency)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, TARGET_SIZE, TARGET_SIZE);

  // Draw cropped image scaled to target size
  ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, TARGET_SIZE, TARGET_SIZE);

  // Encode to JPEG blob
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('Failed to encode image'));
      },
      'image/jpeg',
      JPEG_QUALITY
    );
  });

  return blob;
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image. Try a different file.'));
    };
    img.src = url;
  });
}

/**
 * Full upload flow: process locally, upload to S3 via presigned URL,
 * then save the public URL to the intern record.
 */
export async function uploadProfilePhoto(file, getPhotoUploadUrl, setPhotoUrl) {
  // 1. Process the file
  const blob = await processProfilePhoto(file);

  // 2. Get presigned PUT URL from backend
  const { upload_url, public_url } = await getPhotoUploadUrl();

  // 3. PUT the processed blob directly to S3
  const putRes = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg' },
    body: blob,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => '');
    throw new Error(`Upload failed (${putRes.status}). ${text}`);
  }

  // 4. Save the URL to the intern record (returns cache-busted URL)
  const { photo_url } = await setPhotoUrl(public_url);
  return photo_url;
}
